import React from 'react';
import Head from 'next/head';
import BaseLayout from 'layouts/BaseLayout';
import Hero from 'components/ScientificDiagramMaker/Hero';
import WhyChoose from 'components/ScientificDiagramMaker/WhyChoose';
import FeatureSection from 'components/ScientificDiagramMaker/FeatureSection';
import HowItWorks from 'components/ScientificDiagramMaker/HowItWorks';
import Testimonials from 'components/ScientificDiagramMaker/Testimonials';
import FAQ from 'components/ScientificDiagramMaker/FAQ';
import FinalCTA from 'components/ScientificDiagramMaker/FinalCTA';
import FeatureRecap from 'components/ScientificDiagramMaker/FeatureRecap';
import { COLOR_WHITE } from 'styles/variables/colors';
import { withTranslations } from 'utils/withTranslations';

const FaqPageData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can I make science diagrams for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Mind The Graph offers a free tier that includes access to thousands of illustrations, templates, and basic editing tools. You can create and download diagrams at no cost. For advanced features like watermark-free exports and AI generation, consider upgrading to Prime.',
      },
    },
    {
      '@type': 'Question',
      name: 'What export formats are supported?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can export your diagrams in multiple formats including PNG, PDF, SVG, and TIFF (up to 1200 DPI). Prime users get access to watermark-free exports in all formats, perfect for journal submissions and presentations.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I collaborate with others on my diagrams?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Our platform supports collaborative editing. You can invite team members to work on diagrams together, making it perfect for research teams and co-authored publications.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can i customize the scientific symbols and illustrations?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely! Our PowerEdit tools let you fully customize colors, sizes, positions, and layers. You can also request custom illustrations from our expert design team if you need something specific.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can i use it across different devices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Mind The Graph is a web-based platform that works seamlessly across desktop, tablet, and mobile devices. Your work is automatically saved to the cloud, so you can access it from anywhere.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do i own rights to my diagrams?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Prime users receive full copyright and publication rights for their exported diagrams. You can use them in journal articles, grant proposals, conference presentations, and any other academic or commercial purposes without restrictions.',
      },
    },
  ],
};

const schemaData = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Science Diagram Maker',
  image: 'https://mindthegraph.com/images/mtg-logo-red-editage.svg',
  description:
    'Create professional science diagrams for your research papers, grant proposals, and conference presentations using our intuitive scientific diagram maker with 75,000+ scientifically accurate illustrations across 80+ disciplines.',
  brand: {
    '@type': 'Brand',
    name: 'Mind the Graph',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5',
    bestRating: '5',
    worstRating: '1',
    ratingCount: '3',
    reviewCount: '3',
  },
  review: [
    {
      '@type': 'Review',
      name: 'Best Science Diagram Maker',
      reviewBody:
        'Mind The Graph transformed how I create diagrams for my publications. The library is comprehensive and the editor is incredibly intuitive.',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
      },
      datePublished: '2025-04-09',
      author: {
        '@type': 'Person',
        name: 'Dr. Sarah Chen',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Mind the Graph',
      },
    },
    {
      '@type': 'Review',
      name: 'Online Science Diagram Maker',
      reviewBody:
        'The AI-generated illustrations save me hours of work. The quality is exceptional and perfect for high-impact journals.',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
      },
      datePublished: '2025-11-10',
      author: {
        '@type': 'Person',
        name: 'Prof. Michael Rodriguez',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Mind the Graph',
      },
    },
    {
      '@type': 'Review',
      name: 'Best Scientific Diagram Maker',
      reviewBody:
        'As a student, the free tier is perfect for my thesis. The templates gave me a professional head start on my diagrams.',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
      },
      datePublished: '2025-10-10',
      author: {
        '@type': 'Person',
        name: 'Dr. Lisa Patel',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Mind the Graph',
      },
    },
  ],
};

export default function ScientificDiagramMakerPage() {
  return (
    <BaseLayout
      bgBrandColor={COLOR_WHITE}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>Free Online Science Diagram Maker | Mind the Graph</title>

        <meta
          key="description"
          name="description"
          content="Create professional science diagrams for your research papers, grant proposals, and conference presentations using our intuitive scientific diagram maker with 75,000+ scientifically accurate illustrations across 80+ disciplines."
        />

        <meta
          key="og:title"
          name="og:title"
          property="og:title"
          content="Free Online Science Diagram Maker | Mind the Graph"
        />

        <meta
          key="og:description"
          name="og:description"
          property="og:description"
          content="Create professional science diagrams for your research papers, grant proposals, and conference presentations using our intuitive scientific diagram maker with 75,000+ scientifically accurate illustrations across 80+ disciplines."
        />

        <meta
          key="og:image"
          name="og:image"
          property="og:image"
          content="https://mindthegraph.com/images/scientific-diagram-maker/drag-drop-feature.png"
        />

        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />

        <meta
          key="twitter:title"
          name="twitter:title"
          content="Free Online Science Diagram Maker | Mind the Graph"
        />

        <meta
          key="twitter:description"
          name="twitter:description"
          content="Create professional science diagrams for your research papers, grant proposals, and conference presentations using our intuitive scientific diagram maker with 75,000+ scientifically accurate illustrations across 80+ disciplines."
        />

        <link rel="canonical" href="https://mindthegraph.com/scientific-diagram-maker" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(FaqPageData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>

      <main style={{ minHeight: '100vh', backgroundColor: '#FFFFFF' }}>
        <Hero />
        <WhyChoose />

        <FeatureSection
          title="Largest Scientific Illustrations Library"
          description="Access 75,000+ accurate illustrations across 80+ research fields — medicine, biology, molecular sciences, and more. Our comprehensive library is constantly updated with new scientifically validated icons and diagrams."
          image="/images/scientific-diagram-maker/illustrations-feature.png"
          imageAlt="Scientific illustration library showing colorful medical and biology icons"
          reverse={false}
        />

        <FeatureSection
          title="Intuitive Drag-and-Drop Editor"
          description="Quickly build scientific sketches with powerful editing tools—no design skills needed. Our PowerEdit interface lets you customize every aspect of your diagrams with precision and ease."
          image="/images/scientific-diagram-maker/drag-drop-feature.png"
          imageAlt="Intuitive drag-and-drop editor interface with scientific diagrams"
          reverse={true}
        />

        <FeatureSection
          title="Expert-Validated Visual Templates"
          description="Choose from 300+ templates vetted by domain experts for credibility and journal compliance. Each template is designed to meet the highest standards for scientific publications and presentations."
          image="/images/scientific-diagram-maker/templates-feature-new.png"
          imageAlt="Collection of expert-validated scientific diagram templates"
          reverse={false}
        />

        <FeatureSection
          title="Ready-to-Publish Export Formats"
          description="Download watermark-free PNG, PDF, SVG, or TIFF (1200 DPI) files—perfect for papers, posters, and proposals. Export your diagrams in the exact format required by top scientific journals and conferences."
          image="/images/scientific-diagram-maker/export-feature-new.png"
          imageAlt="High-resolution export format options for scientific diagrams"
          reverse={true}
        />

        <FeatureSection
          title="Custom Visuals by Expert Designers"
          description="Get tailor-made science diagrams in 7 days to perfectly match your research. Our team of professional scientific illustrators works directly with you to create exactly what you need."
          image="/images/scientific-diagram-maker/custom-feature-new.png"
          imageAlt="Custom scientific illustration design service"
          reverse={false}
        />

        <FeatureSection
          title="Unlimited AI-Powered Illustration Generation"
          description="Describe your concept — MTG's AI instantly creates accurate, stylized scientific figures. Transform your research ideas into professional visuals with cutting-edge artificial intelligence."
          image="/images/scientific-diagram-maker/ai-feature-new.png"
          imageAlt="AI-powered scientific illustration generation interface"
          reverse={true}
        />

        <FeatureSection
          title="Full Ownership & Publication Rights"
          description="Prime users enjoy watermark-free exports with complete copyright and publishing freedom. Use your diagrams anywhere without restrictions—from academic papers to commercial presentations."
          image="/images/scientific-diagram-maker/ownership-feature-new.png"
          imageAlt="Full publication rights and ownership of scientific diagrams"
          reverse={false}
        />

        <FeatureRecap />
        <HowItWorks />
        <Testimonials />
        <FAQ />
        <FinalCTA />
      </main>
    </BaseLayout>
  );
}

export const getStaticProps = withTranslations(['common']);
